import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useGlobal } from 'hooks/useGlobal';
import { useQueryState } from 'hooks/useQueryState';
import { ONBOARDING_KEY } from '@/state/onboarding/useOnboarding';
import { BILLING_STATE_KEY, useBillingState } from './useBillingState';
import { AI_CREDIT_METER_KEY } from './useAiCreditMeter';

const POLL_INTERVAL_MS = 3000;
const POLL_LIMIT_MS = 90_000;
const ACTIVATING_KEY = 'BILLING_CHECKOUT_ACTIVATING';
const WAITED_TOO_LONG_KEY = 'BILLING_CHECKOUT_WAITED_TOO_LONG';

type UseCheckoutReturnParams = {
  /** Called once, when the subscription shows up live. Onboarding reports `trial-started` here. */
  onConfirmed?: () => void;
};

/**
 * Polar sends the customer back to `/settings/billing?checkout=plan` or
 * `?checkout=topup` (checkout-session.ts). This handles that return, once.
 *
 * A plan checkout is not active the moment Polar redirects: the subscription
 * reaches our mirror through a webhook, usually a second or two later. Saying
 * "Your subscription is active" on the redirect alone would be claiming
 * something the gate does not yet agree with, and the banner would still be
 * asking for a card underneath it. So the state is polled, every 3s for up to
 * 90s, and the toast waits for it to show a live subscription. Until then the
 * banner and the plan panel say "Activating your subscription..." through
 * `useIsActivatingCheckout`.
 *
 * Called from the ActivationBanner's hook, because the banner is mounted for
 * the whole dashboard session: the poll keeps going if the reader leaves the
 * billing page before it confirms. The param is cleared once handled, so a
 * reload does not replay the toast. Toasts carry ids, so a double-run effect
 * cannot show one twice.
 */
export const useCheckoutReturn = ({ onConfirmed }: UseCheckoutReturnParams = {}) => {
  const queryClient = useQueryClient();
  const [checkout, , , clearCheckout] = useQueryState<string | null>('checkout', null);
  const [, setIsActivating] = useGlobal<boolean>(ACTIVATING_KEY, false);
  const [, setWaitedTooLong] = useGlobal<boolean>(WAITED_TOO_LONG_KEY, false);
  const [timedOut, setTimedOut] = useState(false);

  // An agency buying a client's plan returns to that client's billing view (`billingClient`), and it
  // is that client's plan, not the agency's own, that has to show up. `billingClient` is local to the
  // billing page, so it is read here directly. The agency fee is the agency's own account.
  const [billingClient] = useQueryState<number>('billingClient', 0);
  const accountId = typeof billingClient === 'number' && billingClient > 0 ? billingClient : null;

  const isPlanReturn = checkout === 'plan' && !timedOut;
  const { billingState } = useBillingState({ accountId, refetchInterval: isPlanReturn ? POLL_INTERVAL_MS : false });
  // An agency's own account holds the fee rather than a plan.
  const isLive = billingState?.agencyFee ? billingState.agencyFee.live : !!billingState?.hasLiveSubscription;
  const isConfirmed = isPlanReturn && isLive;
  const isActivating = isPlanReturn && !isConfirmed;

  // Polar adds a customer session token to the address it sends people back to. It opens that
  // customer's portal, so it has no business staying in the address bar, the history or a copied
  // link: it is dropped as soon as the page is up.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('customer_session_token')) return;
    url.searchParams.delete('customer_session_token');
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  useEffect(() => {
    if (checkout !== 'plan' && checkout !== 'topup') return;
    queryClient.invalidateQueries({ queryKey: BILLING_STATE_KEY });
    // A plan grants credits and a top-up adds a pack: the header's AI meter reads both.
    queryClient.invalidateQueries({ queryKey: AI_CREDIT_METER_KEY });
    if (checkout === 'topup') {
      toast.success('Payment received. Your credits can take a minute to show here.', { id: 'billing-checkout-topup' });
      clearCheckout();
    }
  }, [checkout]);

  useEffect(() => {
    if (checkout !== 'plan') return;
    const timer = setTimeout(() => setTimedOut(true), POLL_LIMIT_MS);
    return () => clearTimeout(timer);
  }, [checkout]);

  // Everything on Settings, Billing reads the plan: the Clients table's plan column, the
  // allowance, the credits and the invoices as well as the state that is being polled. Only the
  // state was refreshed, so the rest kept the "no plan" they loaded before the subscription arrived.
  const refreshBilling = () =>
    queryClient.invalidateQueries({
      predicate: (query) => typeof query.queryKey[0] === 'string' && query.queryKey[0].startsWith('BILLING_'),
    });

  useEffect(() => {
    if (!isConfirmed) return;
    onConfirmed?.();
    refreshBilling();
    setWaitedTooLong(false);
    toast.success('Your subscription is active.', { id: 'billing-checkout-plan' });
    // A new account's onboarding moves on to its next step once the plan is confirmed.
    queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });
    clearCheckout();
  }, [isConfirmed]);

  useEffect(() => {
    if (!timedOut || checkout !== 'plan') return;
    // The screens that would otherwise show a live "start" button say the payment is waiting instead.
    setWaitedTooLong(true);
    toast.info('Payment received. Your subscription can take a minute to show here, so check back shortly.', {
      id: 'billing-checkout-plan',
    });
    // Onboarding never refetches on its own; ask once more so a webhook that landed late is seen.
    queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });
    refreshBilling();
    clearCheckout();
  }, [timedOut]);

  useEffect(() => {
    setIsActivating(isActivating);
  }, [isActivating]);
};

/** True between a plan checkout's return and our mirror confirming it. See useCheckoutReturn. */
export const useIsActivatingCheckout = () => {
  const [isActivating] = useGlobal<boolean>(ACTIVATING_KEY, false);
  return isActivating;
};

/**
 * True once a plan checkout has been waited on for the full poll and has still not been confirmed:
 * the payment went through at Polar and our copy of it has not arrived. The screens that ask for a
 * plan show "waiting" with a way to check again, not the plan picker, which would send someone who
 * has already paid back to a checkout.
 */
export const useCheckoutWaitedTooLong = () => {
  const [waited] = useGlobal<boolean>(WAITED_TOO_LONG_KEY, false);
  return waited;
};
